import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { crearNombre, fmtFecha, getNombres } from "../services/egresosService";

const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 32 },
  "& .MuiInputBase-input": { py: "5px", fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

// Alta de nombres para "Dirigido a" / "Depositado a" (espejo de co_cg_nombre). El nombre
// nuevo queda disponible de inmediato en el formulario del cheque (onCreado recarga catálogos).
export default function NombresDialog({ open, onClose, onCreado }) {
  const [lista, setLista] = useState({ total: 0, items: [] });
  const [cargando, setCargando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [buscar, setBuscar] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const cargar = (texto = "") => {
    setCargando(true);
    getNombres({ buscar: texto, limit: 100 })
      .then((res) => setLista(res.data))
      .catch(() => setError("No se pudo cargar la lista de nombres."))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    if (open) {
      setError("");
      setOk("");
      setBuscar("");
      cargar();
    }
  }, [open]);

  const guardar = async () => {
    setGuardando(true);
    setError("");
    setOk("");
    try {
      await crearNombre({ nombre });
      setOk(`Nombre "${nombre.trim()}" guardado.`);
      setNombre("");
      cargar(buscar.trim());
      if (onCreado) onCreado();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo guardar el nombre.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Nombres para cheque</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        {ok && <Alert severity="success" sx={{ mb: 1, py: 0 }}>{ok}</Alert>}
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid size={{ xs: 12, md: 10 }}>
            <TextField fullWidth size="small" label="Descripción (nombre nuevo)" value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && nombre.trim() && !guardando) guardar(); }}
              sx={fieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button fullWidth size="small" variant="contained" onClick={guardar} disabled={guardando || !nombre.trim()} sx={{ height: 32 }}>
              {guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Registrados ({lista.total})</Typography>
          {cargando && <CircularProgress size={14} />}
          <Box sx={{ flexGrow: 1 }} />
          <TextField size="small" placeholder="Buscar..." value={buscar} onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") cargar(buscar.trim()); }}
            sx={{ width: 220, "& .MuiInputBase-input": { py: "3px", fontSize: 12 } }} />
        </Box>
        <TableContainer sx={{ maxHeight: 340 }}>
          <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.2, px: 0.6, fontSize: 11 } }}>
            <TableHead>
              <TableRow>
                {["ID", "Nombre", "Fecha", "Sucursal"].map((h) => <TableCell key={h} sx={{ fontWeight: 800 }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.items.map((n) => (
                <TableRow key={n.id} hover>
                  <TableCell>{n.id}</TableCell><TableCell>{n.nombre}</TableCell>
                  <TableCell>{fmtFecha(n.fecha_registro)}</TableCell><TableCell>{n.sucursal}</TableCell>
                </TableRow>
              ))}
              {!lista.items.length && !cargando && <TableRow><TableCell colSpan={4}>Sin resultados</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {lista.total > lista.items.length && (
          <Typography sx={{ fontSize: 11, mt: 0.5 }} color="text.secondary">
            Mostrando los {lista.items.length} más recientes; usa el buscador para encontrar otros.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}><Button size="small" onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}
